import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WhatsappLogo, EnvelopeSimple, Phone, MapPin, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Communication Center for a customer.
 * Uses free deep-links (wa.me, mailto:, tel:, Google Maps) — no API keys required.
 * Full WhatsApp Business API / SMS gateway / SMTP integration deferred to next iteration.
 */
const TEMPLATES = [
  {
    id: "welcome",
    title: "Welcome",
    body: (c) => `Namaste ${c.name || "Customer"}!\n\nThank you for visiting our CSC / Aaple Sarkar centre. Your customer ID is ${c.customer_no}. We're here to help with all your government service needs.\n\n— DigitalSetu`,
  },
  {
    id: "doc_request",
    title: "Missing Document Request",
    body: (c) => `Namaste ${c.name || "Customer"},\n\nTo complete your application, please share the following document(s) at your earliest convenience:\n- [Document Name]\n\nYou can drop by our centre or WhatsApp a clear photo.\n\nRef: ${c.customer_no}\n— DigitalSetu`,
  },
  {
    id: "app_submitted",
    title: "Application Submitted",
    body: (c) => `Namaste ${c.name || "Customer"},\n\nYour application has been successfully submitted to the portal. We will update you as soon as we have status updates.\n\nRef: ${c.customer_no}\n— DigitalSetu`,
  },
  {
    id: "app_ready",
    title: "Service Completed / Ready for Collection",
    body: (c) => `Good news, ${c.name || "Customer"}!\n\nYour certificate / document is ready for collection at our centre. Please carry an ID proof.\n\nRef: ${c.customer_no}\n— DigitalSetu`,
  },
  {
    id: "payment_reminder",
    title: "Payment Reminder",
    body: (c) => `Namaste ${c.name || "Customer"},\n\nGentle reminder that your outstanding payment is pending against Ref ${c.customer_no}. You can pay via UPI, card or cash at the centre.\n\n— DigitalSetu`,
  },
  {
    id: "complaint_update",
    title: "Complaint Status Update",
    body: (c) => `Namaste ${c.name || "Customer"},\n\nWe wanted to update you on your complaint. Our team is actively working on a resolution and will get back to you shortly.\n\nRef: ${c.customer_no}\n— DigitalSetu`,
  },
  {
    id: "appointment",
    title: "Appointment Reminder",
    body: (c) => `Namaste ${c.name || "Customer"},\n\nYou have an appointment scheduled with us on [Date] at [Time]. Please carry the required documents.\n\nRef: ${c.customer_no}\n— DigitalSetu`,
  },
  {
    id: "blank",
    title: "Blank Message",
    body: () => "",
  },
];

function normalizeMobile(m) {
  if (!m) return "";
  const digits = m.replace(/\D/g, "");
  // If 10 digits assume India, prefix 91
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function mapsLink(customer) {
  const parts = [customer.house_no, customer.street, customer.address, customer.city, customer.taluka, customer.district, customer.state, customer.pin].filter(Boolean);
  const q = encodeURIComponent(parts.join(", "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export default function CommunicationActions({ customer }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("whatsapp"); // whatsapp | email
  const [templateId, setTemplateId] = useState("welcome");
  const [subject, setSubject] = useState("Update from DigitalSetu");
  const [message, setMessage] = useState("");

  const tpl = useMemo(() => TEMPLATES.find((t) => t.id === templateId), [templateId]);

  React.useEffect(() => {
    if (tpl && customer) setMessage(tpl.body(customer));
  }, [tpl, customer]);

  const openWhatsapp = () => { setChannel("whatsapp"); setTemplateId("welcome"); setOpen(true); };
  const openEmail = () => { setChannel("email"); setTemplateId("welcome"); setOpen(true); };

  const send = () => {
    if (channel === "whatsapp") {
      const mobile = normalizeMobile(customer.whatsapp || customer.mobile);
      if (!mobile) { toast.error("No mobile number on record"); return; }
      const url = `https://wa.me/${mobile}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    } else {
      const to = customer.email;
      if (!to) { toast.error("No email on record"); return; }
      const url = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      window.location.href = url;
    }
    setOpen(false);
    toast.success("Message opened — send from your app to complete");
  };

  const btn = "gap-1.5 h-9";

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="comm-actions">
        <Button size="sm" variant="outline" onClick={openWhatsapp} data-testid="comm-whatsapp-btn" className={`${btn} border-emerald-300 text-emerald-700 hover:bg-emerald-50`}>
          <WhatsappLogo size={16} weight="fill" /> WhatsApp
        </Button>
        <Button size="sm" variant="outline" onClick={openEmail} disabled={!customer.email} data-testid="comm-email-btn" className={`${btn} border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50`}>
          <EnvelopeSimple size={16} weight="regular" /> Email
        </Button>
        <a href={`tel:${customer.mobile}`} className="no-underline">
          <Button size="sm" variant="outline" disabled={!customer.mobile} data-testid="comm-call-btn" className={`${btn} border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-50`}>
            <Phone size={16} weight="regular" /> Call
          </Button>
        </a>
        <a href={mapsLink(customer)} target="_blank" rel="noopener noreferrer" className="no-underline">
          <Button size="sm" variant="outline" data-testid="comm-maps-btn" className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>
            <MapPin size={16} weight="regular" /> Open in Maps
          </Button>
        </a>
        <Button size="sm" variant="outline" onClick={() => window.print()} className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>
          <Printer size={16} weight="regular" /> Print
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>
              {channel === "whatsapp" ? "Send WhatsApp Message" : "Send Email"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-slate-500">
              To: <span className="font-mono text-slate-900">{channel === "whatsapp" ? (customer.whatsapp || customer.mobile) : customer.email}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Template</Label>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATES.map((t) => (
                  <button key={t.id} onClick={() => setTemplateId(t.id)}
                    data-testid={`comm-tpl-${t.id}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${templateId === t.id ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-700 border-slate-300 hover:border-orange-400"}`}>
                    {t.title}
                  </button>
                ))}
              </div>
            </div>
            {channel === "email" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Subject</Label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600/30" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Message</Label>
              <Textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="comm-message-input" className="font-sans text-sm" />
            </div>
            <div className="text-[11px] text-slate-500 italic">
              Opens in {channel === "whatsapp" ? "WhatsApp Web / app" : "your default email client"}. Full WhatsApp Business API, SMS gateway and SMTP available in the next update.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="comm-send-btn" onClick={send} className="bg-orange-600 hover:bg-orange-700 text-white">
              {channel === "whatsapp" ? "Open WhatsApp" : "Open Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
